import { makeStyles } from '@material-ui/core/styles';
import React, { useState } from 'react';
import { createStyles, Input, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getTranslate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { stripHtml } from '../../../htmlUtils';

import Arrow from '../../../../common/image/Arrow.png';

const useStyles = makeStyles(theme => createStyles({
    imageContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    column: {
        margin: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    left: {
        backgroundColor: '#778689',
    },
    right: {
        backgroundColor: '#e94f11',
    },
    circle: {
        display: 'flex',
        justifyContent: 'center',
        borderRadius: '50%',
        alignItems: 'center',
        width: 190,
        height: 190,
        color: theme.palette.common.white,

        [theme.breakpoints.down('xs')]: {
            width: 155,
            height: 155,

            '& p': {
                fontSize: '1rem',
            },
        },
    },
    bottomContainer: {
        marginTop: theme.spacing(4),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    arrowImage: {
        margin: theme.spacing(4, 0, 0),
    },
    triangle: {
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        position: 'relative',
        width: 200,
        height: 200,

        '&:after': {
            content: '""',
            position: 'absolute',
            borderLeft: '150px solid transparent',
            borderRight: '150px solid transparent',
            borderBottom: '200px solid #2b7b90',
        },

        [theme.breakpoints.down('xs')]: {
            '& p': {
                fontSize: '1rem',
            },
        },

        '& + $input': {
            width: 300,
        },
    },
    triangleText: {
        zIndex: 1,
        marginTop: 100,
        color: theme.palette.common.white,
    },
    input: {
        marginTop: theme.spacing(2),
        background: theme.palette.grey[200],
        borderRadius: 8,
        padding: theme.spacing(1, 2),
    },
}));

const RouteSelection = ({
    questions, handleResponseUpdate, responses, readOnly,
}) => {
    const [leftValue, setLeftValue] = useState(null);
    const [rightValue, setRightValue] = useState(null);
    const [bottomValue, setBottomValue] = useState(null);
    const classes = useStyles();
    const updateAsyncTimer = React.useRef();
    const translate = useSelector(state => getTranslate(state.localize));

    if (questions?.length !== 3) return null;

    const handleChange = key => (event) => {
        if (readOnly) {
            return;
        }
        const value = event.target.value;
        switch (key) {
        case 0:
            setLeftValue(value);
            break;
        case 1:
            setRightValue(value);
            break;
        case 2:
            setBottomValue(value);
            break;
        default:
            return;
        }

        clearTimeout(updateAsyncTimer.current);
        updateAsyncTimer.current = setTimeout(() => {
            handleResponseUpdate(questions[key])('freetextResponse', value);
        }, 200);
    };

    const answerLeft = leftValue !== null ? leftValue : responses && responses[questions[0].id]?.response?.freetextResponse;
    const answerRight = rightValue !== null ? rightValue : responses && responses[questions[1].id]?.response?.freetextResponse;
    const answerBottom = bottomValue !== null ? bottomValue : responses && responses[questions[2].id]?.response?.freetextResponse;

    return (
        <div>
            <div className={classes.imageContainer}>
                <div className={classes.column}>
                    <div className={classnames(classes.circle, classes.left)}><Typography align="center">{translate('routeSelection.awayValues')}</Typography></div>
                    <img className={classes.arrowImage} src={Arrow} alt="routeSelection.leftArrow" />
                    <Input
                        className={classes.input}
                        placeholder={stripHtml(questions[0].question)}
                        onChange={handleChange(0)}
                        multiline
                        disabled={readOnly}
                        value={answerLeft || ''}
                        disableUnderline
                        fullWidth
                    />
                </div>
                <div className={classes.column}>
                    <div className={classnames(classes.circle, classes.right)}><Typography align="center">{translate('routeSelection.towardsValues')}</Typography></div>
                    <img className={classes.arrowImage} src={Arrow} alt={translate('routeSelection.rightArrow')} />
                    <Input
                        className={classes.input}
                        placeholder={stripHtml(questions[1].question)}
                        onChange={handleChange(1)}
                        multiline
                        disabled={readOnly}
                        value={answerRight || ''}
                        disableUnderline
                        fullWidth
                    />
                </div>
            </div>

            <div className={classes.bottomContainer}>
                <div className={classes.column}>
                    <div className={classes.triangle}><Typography className={classes.triangleText} align="center">{translate('routeSelection.psychologicalFlex')}</Typography></div>
                    <Input
                        className={classes.input}
                        placeholder={stripHtml(questions[2].question)}
                        onChange={handleChange(2)}
                        multiline
                        disabled={readOnly}
                        value={answerBottom || ''}
                        disableUnderline
                        fullWidth
                    />
                </div>
            </div>
        </div>
    );
};

RouteSelection.propTypes = {
    questions: PropTypes.array.isRequired,
    handleResponseUpdate: PropTypes.func.isRequired,
    responses: PropTypes.object,
    readOnly: PropTypes.bool,
};
RouteSelection.defaultProps = {
    responses: null,
    readOnly: false,
};

export default RouteSelection;
